// src/chatbot/components/Chatbot.tsx
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaPaperPlane,
  FaTrash,
  FaMicrophone,
  FaTimes,
  FaStop,
} from "react-icons/fa";
import { FiMaximize2, FiMinimize2, FiX, FiSend } from "react-icons/fi";
import { useBranch } from "~/contexts/BranchContext";
import { createUserSession } from "~/utils/supabaseClient";
import type { MenuItem } from "~/types/new_types";
import { useChatbot } from "../contexts/ChatbotContext";
import ChatbotMessage from "./ChatbotMessage";
import LoadingIndicator from "./LoadingIndicator";
import VoiceRecordingCard from "./VoiceRecordingCard";
import CartButton from "~/cart/Components/CartButton";
import { useBackButtonInterceptor } from "~/utils/backwordButtonFunctionality";

const MAX_RECORDING_SECONDS = 15;

interface ChatbotProps {
  onItemClick?: (item: MenuItem) => void;
}

const Chatbot: React.FC<ChatbotProps> = ({ onItemClick }) => {
  const { t, i18n } = useTranslation();
  const [inputMessage, setInputMessage] = useState("");
  const [isMinimized, setIsMinimized] = useState(false);
  const [isPhysicalKeyboard, setIsPhysicalKeyboard] = useState<boolean | null>(
    null
  );
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const recordingDurationRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const pointerDownInMessagesRef = useRef(false);
  const focusRestoreTimeoutRef = useRef<number | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const recordingButtonRef = useRef<HTMLButtonElement>(null);
  const recordingCancelledRef = useRef(false);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const captureGainNodeRef = useRef<GainNode | null>(null);
  const pcmDataRef = useRef<Float32Array[]>([]);
  const recordingSampleRateRef = useRef<number>(44100);
  const shouldAutoSendRef = useRef(false);
  const { setSelectedItem, theme, selectedItem, branchData } = useBranch();
  const {
    messages,
    isLoading,
    isOpen,
    unreadCount,
    sendMessage,
    sendStreamingMessage,
    clearMessages,
    closeChat,
    toggleChat,
    markAsRead,
    isFullScreen,
    setIsFullScreen,
  } = useChatbot();
  useBackButtonInterceptor(isOpen,closeChat );
  // Track if there's an active streaming assistant message to avoid double indicators
  const hasStreamingAssistant = messages.some(
    (m) => !m.isUser && m.isStreaming
  );

  const [viewportHeight, setViewportHeight] = useState<number>(() =>
    typeof window !== "undefined" ? window.innerHeight : 0
  );
  const [viewportOffsetTop, setViewportOffsetTop] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  // Track visual viewport metrics so the chat floats above the mobile keyboard
  useEffect(() => {
    const updateViewportMetrics = () => {
      if (typeof window === "undefined") return;
      const { visualViewport } = window;
      if (visualViewport) {
        setViewportHeight(visualViewport.height);
        setViewportOffsetTop(visualViewport.offsetTop);
      } else {
        setViewportHeight(window.innerHeight);
        setViewportOffsetTop(0);
      }
    };

    updateViewportMetrics();

    const vv =
      typeof window !== "undefined" ? window.visualViewport : undefined;
    vv?.addEventListener("resize", updateViewportMetrics);
    vv?.addEventListener("scroll", updateViewportMetrics);
    window.addEventListener("resize", updateViewportMetrics);

    return () => {
      vv?.removeEventListener("resize", updateViewportMetrics);
      vv?.removeEventListener("scroll", updateViewportMetrics);
      window.removeEventListener("resize", updateViewportMetrics);
    };
  }, []);

  // Track viewport size so we only lock background scroll for full-screen layouts
  useEffect(() => {
    const handleViewportChange = () => {
      setIsMobileViewport(window.innerWidth < 768);
    };

    handleViewportChange();
    window.addEventListener("resize", handleViewportChange);
    return () => window.removeEventListener("resize", handleViewportChange);
  }, []);

  // Detect keyboard type
  useEffect(() => {
    const detectKeyboardType = () => {
      // Check if device has a physical keyboard
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isDesktopOS =
        /windows|macintosh|linux/.test(userAgent) &&
        !/(android|iphone|ipad|ipod|blackberry|iemobile|opera mini)/.test(
          userAgent
        );
      const hasNoTouch = window.navigator.maxTouchPoints === 0;
      const isLikelyMobile = /mobile|android|iphone|ipad|ipod/.test(userAgent);

      // Physical keyboard if:
      // - Desktop OS without touch capability
      // - OR has keyboard connected (some tablets/hybrids)
      const hasPhysicalKeyboard =
        (isDesktopOS && hasNoTouch) || (!isLikelyMobile && !isMobileViewport);
      setIsPhysicalKeyboard(hasPhysicalKeyboard);
    };

    detectKeyboardType();
  }, [isMobileViewport]);

  const scrollToLatestMessage = (behavior: ScrollBehavior = "auto") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToLatestMessage();
  }, [messages]);

  // Ensure we show the most recent conversation when the chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => scrollToLatestMessage(), 0);
    }
  }, [isOpen, isMinimized]);

  // Focus input when chat opens (but not when recording)
  useEffect(() => {
    if (isOpen && !isMinimized && !isRecording) {
      // Only auto-focus on desktop or when explicitly requested
      if (!isMobileViewport || isPhysicalKeyboard) {
        setTimeout(() => {
          if (inputRef.current && !isRecording) {
            inputRef.current.focus();
          }
        }, 150);
      }
    }
  }, [isOpen, isMinimized, isRecording, isMobileViewport, isPhysicalKeyboard]);

  // Keep focus on textarea when appropriate without interrupting text selection
  useEffect(() => {
    // Only apply aggressive focus management on desktop
    if (
      !isOpen ||
      isMinimized ||
      isLoading ||
      isRecording ||
      isMobileViewport
    ) {
      return;
    }

    const textareaElement = inputRef.current;
    if (!textareaElement) {
      return;
    }

    const attemptFocus = () => {
      if (!inputRef.current) {
        return;
      }
      if (document.activeElement === inputRef.current) {
        return;
      }
      if (isLoading || isRecording) {
        return;
      }
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) {
        return;
      }
      // Only auto-focus on desktop with physical keyboards
      if (
        !isMobileViewport &&
        (isPhysicalKeyboard || isPhysicalKeyboard === null)
      ) {
        inputRef.current.focus();
      }
    };

    const handleBlur = () => {
      if (pointerDownInMessagesRef.current) {
        return;
      }
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) {
        return;
      }
      if (focusRestoreTimeoutRef.current) {
        window.clearTimeout(focusRestoreTimeoutRef.current);
      }
      // Only auto-restore focus on desktop
      if (!isMobileViewport) {
        focusRestoreTimeoutRef.current = window.setTimeout(attemptFocus, 150);
      }
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (messagesContainerRef.current?.contains(event.target as Node)) {
        pointerDownInMessagesRef.current = true;
      }
    };

    const handlePointerUp = () => {
      if (!pointerDownInMessagesRef.current) {
        return;
      }
      const selection = window.getSelection();
      const hasSelection = Boolean(selection && !selection.isCollapsed);
      pointerDownInMessagesRef.current = false;
      if (focusRestoreTimeoutRef.current) {
        window.clearTimeout(focusRestoreTimeoutRef.current);
      }
      // Only auto-restore focus on desktop and if no text is selected
      if (!hasSelection && !isMobileViewport) {
        focusRestoreTimeoutRef.current = window.setTimeout(attemptFocus, 120);
      }
    };

    textareaElement.addEventListener("blur", handleBlur);
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("pointerup", handlePointerUp);

    return () => {
      textareaElement.removeEventListener("blur", handleBlur);
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("pointerup", handlePointerUp);
      if (focusRestoreTimeoutRef.current) {
        window.clearTimeout(focusRestoreTimeoutRef.current);
        focusRestoreTimeoutRef.current = null;
      }
    };
  }, [
    isOpen,
    isMinimized,
    isLoading,
    isRecording,
    isMobileViewport,
    isPhysicalKeyboard,
  ]);

  useEffect(() => {
    if (selectedItem) {
      document.body.style.overflow = "visible";
      return () => {
        document.body.style.overflow = "visible";
      };
    }

    const shouldLockScroll =
      isOpen && !isMinimized && (isMobileViewport || isFullScreen);
    if (shouldLockScroll) {
      scrollToLatestMessage();
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "visible";
    }

    return () => {
      document.body.style.overflow = "visible";
    };
  }, [isOpen, isMinimized, isFullScreen, isMobileViewport, selectedItem]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const message = inputMessage.trim();
    setInputMessage("");

    if (inputRef.current) {
      if (isMobileViewport) {
        inputRef.current.blur();
      } else {
        // Keep desktop focus so users can continue typing immediately
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
    // Use streaming for better UX
    await sendStreamingMessage(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      if (isPhysicalKeyboard) {
        // Physical keyboard: Enter sends, Shift+Enter for new line
        if (!e.shiftKey) {
          e.preventDefault();
          handleSendMessage();
        }
        // Let Shift+Enter pass through for new line
      } else {
        // Virtual keyboard: Enter for new line only
        // Don't prevent default, allow new line
      }
    }
  };

  const handleItemClick = (item: MenuItem) => {
    onItemClick?.(item);
    // Optionally close chat after item selection
    // closeChat();
  };

  const handleClearChat = async () => {
    if (branchData?.branch?.id) {
      try {
        await createUserSession(branchData.branch.id);
      } catch (error) {
        console.error("Failed to create a new session:", error);
      }
    }

    clearMessages();
  };

  // Voice Recording Functions
  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        // Extract just the base64 part (remove data:audio/webm;base64, prefix)
        const base64String = base64.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const audioBufferToWav = (audioBuffer: AudioBuffer): ArrayBuffer => {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const samples = audioBuffer.length;
    const bytesPerSample = 2; // 16-bit PCM
    const blockAlign = numberOfChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = samples * blockAlign;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    let offset = 0;
    const writeString = (value: string) => {
      for (let i = 0; i < value.length; i++) {
        view.setUint8(offset + i, value.charCodeAt(i));
      }
      offset += value.length;
    };

    writeString("RIFF");
    view.setUint32(offset, 36 + dataSize, true);
    offset += 4;
    writeString("WAVE");
    writeString("fmt ");
    view.setUint32(offset, 16, true);
    offset += 4;
    view.setUint16(offset, 1, true);
    offset += 2;
    view.setUint16(offset, numberOfChannels, true); // NumChannels
    offset += 2;
    view.setUint32(offset, sampleRate, true); // SampleRate
    offset += 4;
    view.setUint32(offset, byteRate, true); // ByteRate
    offset += 4;
    view.setUint16(offset, blockAlign, true); // BlockAlign
    offset += 2;
    view.setUint16(offset, 16, true); // BitsPerSample
    offset += 2;
    writeString("data"); // Subchunk2ID
    view.setUint32(offset, dataSize, true); // Subchunk2Size
    offset += 4;

    const channelData: Float32Array[] = [];
    for (let channel = 0; channel < numberOfChannels; channel += 1) {
      channelData.push(audioBuffer.getChannelData(channel));
    }

    let sampleIndex = 0;
    for (let i = 0; i < samples; i += 1) {
      for (let channel = 0; channel < numberOfChannels; channel += 1) {
        let sample = channelData[channel][i];
        sample = Math.max(-1, Math.min(1, sample));
        const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        view.setInt16(44 + sampleIndex, intSample, true);
        sampleIndex += bytesPerSample;
      }
    }

    return buffer;
  };

  const mergeFloat32Arrays = (chunks: Float32Array[]): Float32Array => {
    if (!chunks.length) {
      return new Float32Array(0);
    }
    const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const result = new Float32Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
    return result;
  };

  const createWavBlobFromPcm = (
    pcmChunks: Float32Array[],
    sampleRate: number,
    audioContext?: AudioContext | null
  ): Blob | null => {
    const merged = mergeFloat32Arrays(pcmChunks);
    if (!merged.length) {
      return null;
    }
    const channelData = Float32Array.from(merged);

    let audioBuffer: AudioBuffer | null = null;
    if (typeof AudioBuffer !== "undefined") {
      try {
        audioBuffer = new AudioBuffer({
          length: merged.length,
          numberOfChannels: 1,
          sampleRate,
        });
        audioBuffer.copyToChannel(channelData, 0);
      } catch (error) {
        audioBuffer = null;
      }
    }

    if (!audioBuffer && audioContext) {
      const buffer = audioContext.createBuffer(1, merged.length, sampleRate);
      buffer.copyToChannel(channelData, 0);
      audioBuffer = buffer;
    }

    if (!audioBuffer) {
      const typedWindow = window as typeof window & {
        webkitAudioContext?: typeof AudioContext;
      };
      const AudioContextConstructor =
        window.AudioContext || typedWindow.webkitAudioContext;
      if (!AudioContextConstructor) {
        return null;
      }
      const tempContext = new AudioContextConstructor();
      const buffer = tempContext.createBuffer(1, merged.length, sampleRate);
      buffer.copyToChannel(channelData, 0);
      audioBuffer = buffer;
      tempContext.close();
    }

    if (!audioBuffer) {
      return null;
    }

    const wavArrayBuffer = audioBufferToWav(audioBuffer);
    return new Blob([wavArrayBuffer], { type: "audio/wav" });
  };

  const decodeAudioData = async (
    arrayBuffer: ArrayBuffer
  ): Promise<AudioBuffer> => {
    const AudioContextConstructor =
      window.AudioContext ||
      (window as typeof window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioContextConstructor) {
      throw new Error("Web Audio API is not supported in this browser");
    }
    const audioContext = new AudioContextConstructor();
    try {
      const bufferCopy = arrayBuffer.slice(0);
      const decodeResult = audioContext.decodeAudioData(bufferCopy);
      const audioBuffer =
        decodeResult instanceof Promise
          ? await decodeResult
          : await new Promise<AudioBuffer>((resolve, reject) => {
              audioContext.decodeAudioData(
                arrayBuffer.slice(0),
                resolve,
                reject
              );
            });
      await audioContext.close();
      return audioBuffer;
    } catch (error) {
      await audioContext.close();
      throw error;
    }
  };

  const convertBlobToPreferredFormat = async (
    blob: Blob
  ): Promise<{ blob: Blob; format: "wav" | "ogg" | "webm" }> => {
    if (blob.type && blob.type.toLowerCase().includes("wav")) {
      return { blob, format: "wav" };
    }

    try {
      const arrayBuffer = await blob.arrayBuffer();
      const audioBuffer = await decodeAudioData(arrayBuffer);
      const wavBuffer = audioBufferToWav(audioBuffer);
      return {
        blob: new Blob([wavBuffer], { type: "audio/wav" }),
        format: "wav",
      };
    } catch (error) {
      console.warn(
        "Failed to convert audio to WAV format. Falling back to original blob.",
        error
      );
      if (blob.type) {
        const lowerType = blob.type.toLowerCase();
        if (lowerType.includes("ogg")) {
          return { blob, format: "ogg" };
        }
        if (lowerType.includes("webm")) {
          return { blob, format: "webm" };
        }
      }
      return { blob, format: "wav" };
    }
  };

  const clearRecordingStartTimeout = () => {
    // No longer needed for one-click recording
  };

  const resetRecordingStates = () => {
    setIsRecording(false);
    setRecordingDuration(0);
    setAudioLevel(0);
    setAudioBlob(null);
    recordingDurationRef.current = 0;
    audioChunksRef.current = [];
    pcmDataRef.current = [];
    recordingSampleRateRef.current = 44100;
    shouldAutoSendRef.current = false;

    // Clean up media stream
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
      setMediaStream(null);
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    recordingCancelledRef.current = false;
    mediaRecorderRef.current = null;
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current.onaudioprocess = null;
      scriptProcessorRef.current = null;
    }
    if (captureGainNodeRef.current) {
      captureGainNodeRef.current.disconnect();
      captureGainNodeRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
  };

  const updateAudioLevel = () => {
    if (!analyserRef.current) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Calculate the average audio level with better sensitivity
    let sum = 0;
    for (let i = 0; i < bufferLength; i++) {
      sum += dataArray[i];
    }
    const average = sum / bufferLength;
    // More sensitive normalization (0-1) with amplification
    const normalizedLevel = Math.min((average / 80) * 1.5, 1);

    setAudioLevel(normalizedLevel);

    if (isRecording) {
      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    }
  };

  const handleRecordingLimitReached = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      return;
    }
    // Stop recording and automatically send when limit is reached
    stopRecording();
    // Set a flag to auto-send after recording stops
    setTimeout(async () => {
      if (audioBlob) {
        try {
          const { blob: preparedBlob, format } =
            await convertBlobToPreferredFormat(audioBlob);
          await sendVoiceMessage(preparedBlob, format);
        } catch (error) {
          console.error("Error sending voice message:", error);
          alert("Failed to send voice message");
          resetRecordingStates();
        }
      }
    }, 100); // Small delay to ensure audioBlob is set
  };

  const startRecording = async () => {
    try {
      // Blur the input to prevent keyboard from staying open on mobile
      if (inputRef.current) {
        inputRef.current.blur();

        // On mobile, ensure the virtual keyboard is dismissed
        if (isMobileViewport) {
          // Force blur and wait a bit for keyboard to dismiss
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.blur();
            }
          }, 50);
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMediaStream(stream);
      setAudioBlob(null);

      // Create audio context for visualization and PCM capture
      const AudioContextConstructor =
        window.AudioContext ||
        (window as typeof window & { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (AudioContextConstructor) {
        const audioContext = new AudioContextConstructor();
        audioContextRef.current = audioContext;
        recordingSampleRateRef.current = audioContext.sampleRate;
        pcmDataRef.current = [];

        const source = audioContext.createMediaStreamSource(stream);
        analyserRef.current = audioContext.createAnalyser();
        source.connect(analyserRef.current);
        analyserRef.current.fftSize = 512; // Increased for more sensitivity

        const gainNode = audioContext.createGain();
        gainNode.gain.value = 0;
        captureGainNodeRef.current = gainNode;

        if (typeof audioContext.createScriptProcessor === "function") {
          const processor = audioContext.createScriptProcessor(
            4096,
            source.channelCount || 1,
            source.channelCount || 1
          );
          scriptProcessorRef.current = processor;
          processor.onaudioprocess = (event) => {
            const inputData = event.inputBuffer.getChannelData(0);
            pcmDataRef.current.push(Float32Array.from(inputData));
          };
          source.connect(processor);
          processor.connect(gainNode);
          gainNode.connect(audioContext.destination);
        } else {
          scriptProcessorRef.current = null;
          captureGainNodeRef.current = null;
        }

        updateAudioLevel();
      }

      const preferredMimeTypes = [
        "audio/webm;codecs=opus",
        "audio/ogg;codecs=opus",
        "audio/webm",
        "audio/ogg",
      ] as const;

      let options: MediaRecorderOptions | undefined;
      for (const mimeType of preferredMimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          options = { mimeType };
          break;
        }
      }

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      recordingCancelledRef.current = false;
      setIsRecording(true);
      setRecordingDuration(0);
      recordingDurationRef.current = 0;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data?.size) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        mediaRecorderRef.current = null;
        if (scriptProcessorRef.current) {
          scriptProcessorRef.current.disconnect();
          scriptProcessorRef.current.onaudioprocess = null;
          scriptProcessorRef.current = null;
        }
        if (captureGainNodeRef.current) {
          captureGainNodeRef.current.disconnect();
          captureGainNodeRef.current = null;
        }
        const wasCancelled = recordingCancelledRef.current;
        const shouldAutoSend = shouldAutoSendRef.current;
        recordingCancelledRef.current = false;
        shouldAutoSendRef.current = false;

        if (wasCancelled) {
          pcmDataRef.current = [];
          audioChunksRef.current = [];
          resetRecordingStates();
          return;
        }

        const activeSampleRate =
          recordingSampleRateRef.current ||
          audioContextRef.current?.sampleRate ||
          44100;
        const wavBlob = createWavBlobFromPcm(
          pcmDataRef.current,
          activeSampleRate,
          audioContextRef.current
        );
        pcmDataRef.current = [];

        try {
          const mimeType =
            mediaRecorder.mimeType || options?.mimeType || "audio/webm";
          const fallbackBlob = audioChunksRef.current.length
            ? new Blob(audioChunksRef.current, { type: mimeType })
            : null;
          audioChunksRef.current = [];
          const recordedBlob = wavBlob && wavBlob.size ? wavBlob : fallbackBlob;

          if (
            !recordedBlob ||
            !recordedBlob.size ||
            recordingDurationRef.current === 0
          ) {
            resetRecordingStates();
            return;
          }

          if (shouldAutoSend) {
            // Send immediately without showing the UI
            try {
              const conversion = await convertBlobToPreferredFormat(
                recordedBlob
              );
              const base64Audio = await blobToBase64(conversion.blob);
              const contentType =
                conversion.format === "ogg"
                  ? "audio/ogg"
                  : conversion.format === "webm"
                  ? "audio/webm"
                  : "audio/wav";
              await sendStreamingMessage("", base64Audio, contentType);
              resetRecordingStates();
            } catch (error) {
              console.error("Error sending voice message:", error);
              alert("Failed to send voice message");
              resetRecordingStates();
            }
          } else {
            // Set the audio blob for visualization instead of sending immediately
            setAudioBlob(recordedBlob);
            setIsRecording(false);
          }
        } catch (processError) {
          console.error("Failed to process recorded audio", processError);
          alert("Unable to process recorded audio. Please try again.");
          resetRecordingStates();
        }
      };

      mediaRecorder.start();
      audioChunksRef.current = [];

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => {
          const next = prev + 1;
          const clamped = Math.min(next, MAX_RECORDING_SECONDS);
          recordingDurationRef.current = clamped;
          if (next >= MAX_RECORDING_SECONDS) {
            handleRecordingLimitReached();
          }
          return clamped;
        });
      }, 1000);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Unable to access microphone. Please check permissions.");
      resetRecordingStates();
    }
  };

  const stopRecording = (autoSend = false) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      return;
    }

    // Set flag to auto-send when recording completes
    shouldAutoSendRef.current = autoSend;

    recorder.stop();
    setIsRecording(false);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    // Focus back to input after recording stops - only on desktop or when using physical keyboard
    if (inputRef.current && (!isMobileViewport || isPhysicalKeyboard)) {
      setTimeout(() => {
        if (inputRef.current && !isRecording) {
          inputRef.current.focus();
        }
      }, 200);
    }
  };

  const cancelRecording = () => {
    recordingCancelledRef.current = true;
    audioChunksRef.current = [];
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
      return;
    }

    mediaRecorderRef.current = null;
    resetRecordingStates();

    // Focus back to input after cancelling recording - only on desktop or when using physical keyboard
    if (inputRef.current && (!isMobileViewport || isPhysicalKeyboard)) {
      setTimeout(() => {
        if (inputRef.current && !isRecording) {
          inputRef.current.focus();
        }
      }, 200);
    }
  };

  const handleRecordButtonClick = () => {
    if (isLoading) return;

    if (isRecording) {
      // Stop recording and send
      stopRecording();
    } else {
      // Start recording
      startRecording();
    }
  };

  const sendVoiceMessage = async (
    blobOverride?: Blob,
    formatOverride?: "wav" | "ogg" | "webm"
  ) => {
    const blobToSend = blobOverride ?? audioBlob;
    if (!blobToSend) {
      return;
    }

    try {
      const conversion = formatOverride
        ? { blob: blobToSend, format: formatOverride }
        : await convertBlobToPreferredFormat(blobToSend);
      const base64Audio = await blobToBase64(conversion.blob);
      const contentType =
        conversion.format === "ogg"
          ? "audio/ogg"
          : conversion.format === "webm"
          ? "audio/webm"
          : "audio/wav";

      await sendStreamingMessage("", base64Audio, contentType);
      resetRecordingStates();
    } catch (error) {
      console.error("Error sending voice message:", error);
      alert("Failed to send voice message");
      resetRecordingStates();
    }
  };

  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Voice Recording Card Handlers
  const handleVoiceRecordingCancel = () => {
    cancelRecording();
  };

  const handleVoiceRecordingSend = async () => {
    if (isRecording) {
      // If still recording, stop it and auto-send
      stopRecording(true);
    } else if (audioBlob) {
      // If recording is already stopped, just send the existing blob
      try {
        const { blob: preparedBlob, format } =
          await convertBlobToPreferredFormat(audioBlob);
        await sendVoiceMessage(preparedBlob, format);
      } catch (error) {
        console.error("Error sending voice message:", error);
        alert("Failed to send voice message");
        resetRecordingStates();
      }
    }
  };

  // Auto-resize textarea
  const handleInputChange = () => {
    if (!inputRef.current) return;

    // Auto-resize textarea
    inputRef.current.style.height = "auto";
    inputRef.current.style.height =
      Math.min(inputRef.current.scrollHeight, 120) + "px";
  };

  const handleInputFocus = () => {
    if (!isMobileViewport) return;

    // On mobile, scroll to latest message when input gains focus
    setTimeout(() => {
      scrollToLatestMessage("smooth");

      // Ensure the input is visible above the virtual keyboard
      if (inputRef.current) {
        inputRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    }, 150);
  };

  useEffect(() => {
    handleInputChange();
  }, [inputMessage]);

  if (!theme) return null;

  const containerStyle: React.CSSProperties = {
    backgroundColor: theme.backgroundColor,
    borderRadius: isFullScreen ? "" : theme.borderRadius,
    border:
      !isFullScreen && theme.cardStyle === "bordered"
        ? `2px solid ${theme.borderColor}`
        : "none",
    boxShadow: `0 20px 50px ${theme.primaryColor}30`,
  };

  if (isMobileViewport) {
    containerStyle.height = `${viewportHeight}px`;
    containerStyle.top = `${viewportOffsetTop}px`;
    containerStyle.bottom = "auto";
  }

  // Chat Button (when closed)
  if (!isOpen) {
    return (
      <motion.button
        onClick={toggleChat}
        className={`fixed bottom-6 z-20 w-14 h-14 rounded-full flex items-center justify-center cursor-pointer ${
          i18n.language === "ar" ? "left-6" : "right-6"
        }`}
        style={{
          backgroundColor: theme.primaryColor,
          color: "#ffffff",
          boxShadow: `0 8px 25px ${theme.primaryColor}40`,
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 500, damping: 20 }}
      >
        <img
          src="/test-envato-labs-image-edit_5.webp"
          style={{
            height: "70px",
            width: "70px",
            filter: "brightness(0) invert(1)", // Makes image white to match button text
            objectFit: "contain",
          }}
          alt="AI Assistant"
        />
        {/* Unread count badge */}
        {unreadCount > 0 && (
          <motion.div
            className={`absolute -top-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
              i18n.language === "ar" ? "-left-2" : "-right-2"
            }`}
            style={{ backgroundColor: theme.secondaryColor }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 20 }}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </motion.div>
        )}
      </motion.button>
    );
  }

  // Chat Interface (when open)
  return (
    <motion.div
      ref={contentRef}
      className={`agent-chatbot-container fixed z-30 flex flex-col overflow-hidden shadow-2xl ${
        // Mobile: full screen, Desktop: floating window
        "inset-0  " +
        "w-full " +
        (isFullScreen
          ? ""
          : `md:inset-auto md:bottom-6  ${
              i18n.language === "ar" ? "md:left-6" : "md:right-6"
            } md:rounded-2xl md:w-96 md:max-w-sm ` +
            (isMinimized ? "md:h-[70px]" : "md:max-h-[500px]"))
      }`}
      style={containerStyle}
      initial={{
        scale: 1,
        y: "100%", // Start from bottom on mobile
        x: 0,
      }}
      animate={{
        scale: 1,
        y: 0,
        x: 0,
      }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 border-b relative"
        style={{
          backgroundColor: theme.primaryColor,
          borderColor: `${theme.borderColor}30`,
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{
              backgroundColor: "rgba(255,255,255,0.2)",
              color: "#ffffff",
            }}
          >
            <img
              src="/test-envato-labs-image-edit_5.webp"
              style={{
                height: "50px",
                width: "50px",
                filter: "brightness(0) invert(1)",
                objectFit: "contain",
              }}
              alt="AI Assistant"
            />
          </div>

          <div>
            <h3 className=" text-white text-xl" style={{ fontFamily: theme.titleFontFamily }}>
              {branchData?.summary?.agent_info?.[0]?.agent_name || t("chatbot.title")}
            </h3>
            <p className="text-xs text-white/80" style={{ fontFamily: theme.fontFamily }}>
              {isPhysicalKeyboard !== null &&
                (isPhysicalKeyboard
                  ? t("chatbot.physicalKeyboardHint")
                  : t("chatbot.virtualKeyboardHint"))}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Clear Chat Button */}
          {messages.length > 0 && (
            <motion.button
              onClick={handleClearChat}
              className="flex w-6 h-6 rounded-full items-center justify-center text-white hover:bg-white/20 cursor-pointer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title={t("chatbot.clearChat")}
            >
              <FaTrash size={14} />
            </motion.button>
          )}

          {/* Minimize button - only show on desktop */}
          {/* <motion.button
						onClick={() => setIsMinimized(!isMinimized)}
						className="hidden md:flex w-6 h-6 rounded-full items-center justify-center text-white hover:bg-white/20 cursor-pointer"
						whileHover={{ scale: 1.1 }}
						whileTap={{ scale: 0.9 }}
					>
						{isMinimized ? <FaExpand size={12} /> : <FaMinus size={12} />}
					</motion.button> */}

          {/* Maximize button - only show on desktop */}

          <motion.button
            onClick={() => setIsFullScreen((prev) => !prev)}
            className="hidden md:flex w-6 h-6 rounded-full items-center justify-center text-white hover:bg-white/20 cursor-pointer"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title={isFullScreen ? t("chatbot.minimize") : t("chatbot.maximize")}
          >
            {isFullScreen ? (
              <FiMinimize2 size={14} />
            ) : (
              <FiMaximize2 size={14} />
            )}
          </motion.button>

          {/* Cart Button */}
          <CartButton position="header" onItemClick={setSelectedItem} />

          <motion.button
            onClick={closeChat}
            className="w-6 h-6 bg-gray-100/20 rounded-full flex items-center justify-center text-white hover:bg-white/20 cursor-pointer"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title={t("chatbot.close")}
          >
            <FiX size={14} />
          </motion.button>
        </div>
      </div>

      {/* Chat Content (hidden when minimized) */}
      <AnimatePresence>
        {!isMinimized && (
          <motion.div
            className="flex flex-col flex-1 "
            initial={{ height: 0, opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Messages Area */}
            <div
              ref={messagesContainerRef}
              className={`flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4 min-h-[265px] ${
                isRecording || audioBlob !== null 
                  ? "max-h-[calc(100vh-280px)]" 
                  : "max-h-[calc(100vh-140px)]"
              } ${
                isFullScreen ? "" : isRecording || audioBlob !== null 
                  ? "md:max-h-[220px]" 
                  : "md:max-h-[360px]"
              }`}
              style={{
                backgroundColor: theme.backgroundColor,
              }}
            >
              {messages.length === 0 && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-8 "
                >
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{
                      backgroundColor: `${theme.primaryColor}`,
                      color: theme.primaryColor,
                    }}
                  >
                    <img
                      src="/test-envato-labs-image-edit_5.webp"
                      style={{
                        height: "70px",
                        width: "70px",
                        filter: "brightness(0) invert(1)",
                        objectFit: "contain",
                      }}
                      alt="AI Assistant"
                    />
                  </div>
                  <h4
                    className="font-semibold mb-2"
                    
                    style={{color: theme.textColor }}
                  >
                    {t("chatbot.welcome")}
                  </h4>
                  <p
                    className="text-sm"
                    style={{ color: `${theme.textColor}80`, fontFamily: theme.fontFamily }}
                  >
                    {t("chatbot.welcomeMessage")}
                  </p>
                </motion.div>
              )}

              {messages.map((message) => (
                <ChatbotMessage
                  key={message.id}
                  message={message}
                  theme={theme}
                  onItemClick={handleItemClick}
                />
              ))}

              {/* Show generic loading only if no streaming bubble is visible */}
              {isLoading && !hasStreamingAssistant && (
                <LoadingIndicator theme={theme} />
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div
              className={`p-4 border-t w-full ${
                isRecording || audioBlob !== null ? "pb-2" : ""
              }`}
              style={{
                backgroundColor: theme.cardColor,
                borderColor: `${theme.borderColor}30`,
                
              }}
            >
              {/* Voice Recording Card */}
              <VoiceRecordingCard
                isVisible={isRecording || audioBlob !== null}
                isRecording={isRecording}
                recordingDuration={recordingDuration}
                audioBlob={audioBlob}
                mediaStream={mediaStream}
                theme={theme}
                onCancel={handleVoiceRecordingCancel}
                onSend={handleVoiceRecordingSend}
                formatRecordingTime={formatRecordingTime}
              />

              <div className="flex items-end gap-3 pb-2">
                {/* Message Input */}
                <div className="flex-1 flex items-end relative">
                  <textarea
                    ref={inputRef}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      isRecording
                        ? "Recording..."
                        : isPhysicalKeyboard
                        ? t("chatbot.inputPlaceholderPhysical")
                        : t("chatbot.inputPlaceholderVirtual")
                    }
                    disabled={isRecording}
                    rows={1}
                    onFocus={handleInputFocus}
                    className="w-full px-4 py-2 border focus:outline-none focus:ring-2 transition-all resize-none min-h-[40px] max-h-[120px] no-scrollbar"
                    style={
                      {
                        backgroundColor: theme.backgroundColor,
                        color: theme.textColor,
                        borderColor: `${theme.borderColor}40`,
                        borderRadius: theme.borderRadius,
                        "--tw-ring-color": `${theme.primaryColor}40`,
                        opacity: isRecording ? 0.5 : 1,
                      } as React.CSSProperties
                    }
                  />
                </div>

                {/* Send Button or Voice Recorder */}
                {inputMessage.trim() ? (
                  <motion.button
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    whileHover={{
                      scale: inputMessage.trim() && !isLoading ? 1.1 : 1,
                    }}
                    whileTap={{
                      scale: inputMessage.trim() && !isLoading ? 0.9 : 1,
                    }}
                    style={{
                      backgroundColor: theme.primaryColor,
                      color: "#ffffff",
                      borderRadius: theme.borderRadius,
                    }}
                  >
                    <FaPaperPlane size={14} />
                  </motion.button>
                ) : (
                  <motion.button
                    ref={recordingButtonRef}
                    onClick={handleRecordButtonClick}
                    disabled={isLoading}
                    className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 transition-all"
                    animate={isRecording ? { scale: 1.1 } : { scale: 1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      backgroundColor: isRecording
                        ? theme.secondaryColor
                        : theme.primaryColor,
                      color: "#ffffff",
                      borderRadius: theme.borderRadius,
                    }}
                    title={
                      isRecording
                        ? "Recording in progress..."
                        : "Click to start voice recording"
                    }
                  >
                    {isRecording ? (
                      <FaStop size={12} />
                    ) : (
                      <FaMicrophone size={14} />
                    )}
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Chatbot;
